import React, { createContext, useState, useMemo, useEffect } from "react";
import { fetchUrlTOken, getUrlParams } from "app/store/dataUtils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { appConfig } from "../../../configs/appConfig";
import { usePopupStore } from "app/store/popupStore";
import { useAuthStore } from "app/store/userStore";
import { useDataStore } from "app/store/dataStore";
import { IImages, IMessage } from "src/app/types";
import { atlasApp } from "./initAtlas";
import * as Realm from "realm-web";
import { User } from "../../user";
import { BSON as Oid } from "realm-web";
import axios from "axios";
import { io } from "socket.io-client";
import { useSocketStore } from "app/store/socketStore";

const ValidateErrors = [
  "Cannot access member 'token' of undefined",
  "Invalid Credentials",
  "Expired Token",
];

export type AtlasAuthStatus =
  | "configuring"
  | "authenticated"
  | "unauthenticated";

export type Tokens = {
  env: string;
  token: string;
};

export type AtlasAuthContextType = {
  authStatus: AtlasAuthStatus;
  isLoading: boolean;
  user?: User;
  signOut?: () => void;
  refreshToken?: () => void;
  retryValidation: () => Promise<boolean>;
  setIsLoading?: (T: boolean) => void;
  signInAnon?: (
    trackingRef: string,
  ) => Promise<
    Realm.User<
      globalThis.Realm.DefaultFunctionsFactory &
        globalThis.Realm.BaseFunctionsFactory,
      SimpleObject,
      globalThis.Realm.DefaultUserProfileData
    >
  >;
  sendChatMessage: (
    message: Partial<IMessage>,
  ) => Promise<globalThis.Realm.Services.MongoDB.InsertOneResult<any>>;
  sendChatsMessageImages: (
    images: Partial<IImages>[],
    message: Partial<IMessage>,
  ) => Promise<globalThis.Realm.Services.MongoDB.InsertManyResult<any>>;
  fetchChatMessages: () => Promise<any>;
  fetchChatMessageImages: (chatMessageId: string) => Promise<string>;
};

const defaultAuthContext: AtlasAuthContextType = {
  authStatus: "configuring",
  refreshToken: null,
  signInAnon: null,
  isLoading: false,
  signOut: null,
  user: null,
  setIsLoading: () => {},
  retryValidation: async () => {
    return false;
  },
  sendChatMessage: async () => {
    return { insertedId: null };
  },
  sendChatsMessageImages: async () => {
    return { insertedIds: [] };
  },
  fetchChatMessages: async () => {
    return [];
  },
  fetchChatMessageImages: async () => "",
};

export const AtlasAuthContext =
  createContext<AtlasAuthContextType>(defaultAuthContext);

export type AtlasAuthProviderProps = { children: React.ReactNode };

function AtlasAuthProvider(props: AtlasAuthProviderProps) {
  const { setChatId } = useDataStore();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>({});
  const [params] = useSearchParams();
  const { children } = props;
  const navigate = useNavigate();

  const { setPopState, setChatsLoading, setChatIdFound } = usePopupStore();
  const { setAuthenticated, setValidationFailed } = useAuthStore();
  const { setChatsData, setDriverAndWarehouse } = useDataStore();
  const { messageSocket, setMessageSocket } = useSocketStore();

  // useEffect(() => {
  //     // signInAnon()
  //     getChatsId()
  // }, [])

  // useEffect(() => {
  //     try {
  //         if (messageSocket) {
  //             const { shipment, chatId } = getUrlParams();
  //             // console.log({ chatId })
  //             messageSocket.emit('messages::fetch', { chat_id: chatId }, () => { });

  //             messageSocket.on("messages", async (data) => {
  //                 console.log({ data })
  //                 if (data.length > 0) {
  //                     setChatsData(data)
  //                     setChatsLoading(false);
  //                     setPopState("appAlert", false)
  //                     if (!shipment) await updateMessageSeen(chatId);
  //                     await subscribeToChats(chatId, shipment);
  //                 }
  //                 else {
  //                     setChatsLoading(false);
  //                     setPopState("appAlert", true)
  //                     setPopState("appError", true)
  //                 }
  //                 setTimeout(() => setPopState("appAlert", false), 4000);
  //             });

  //             messageSocket.on("message-update", (data) => {
  //                 console.log("Message update received:", data);
  //             });
  //             messageSocket.on("connect_error", (err) => {
  //                 //handle connection error.
  //             });
  //         }
  //     } catch (error) {
  //         //handle err.
  //     }
  //     return () => {
  //         messageSocket && messageSocket.disconnect();
  //     };
  // }, [messageSocket])

  // const validateTokens = async () => {
  //     try {
  //         const { env, token } = fetchUrlTOken()
  //         const validateResult = await atlasApp.currentUser?.functions.validateRCToken(token, env);
  //         if (ValidateErrors.includes(validateResult?.result)) {
  //             setAuthenticated(false);
  //             setValidationFailed(true);
  //             setPopState("validation", true);
  //             return false;
  //         }
  //         setAuthenticated(true);
  //         return true;
  //     } catch (error) {
  //         setPopState("validation", true)
  //         setAuthenticated(false);
  //         setValidationFailed(true);
  //         return false;
  //     }
  // }

  const appendToUri = (param: string) => {
    if (!params.get("chatId"))
      navigate(`/${window.location.search} + ${param}`, { replace: true });
  };

  const getChatsId = async () => {
    const { chatId, shipment, driver, warehouse } = getUrlParams();
    try {
      if (chatId) {
        setChatId(chatId);
        setChatIdFound(true);
        await startSocketConnection(chatId);
        return;
      }

      const response = await axios.get(
        "http://localhost:3000/api/chats/chat_id",
        {
          params: { driver, warehouse, shipment },
        },
      );
      const newChatId = response.data;

      if (newChatId) {
        setChatId(newChatId);
        appendToUri(`&chatId=${new Oid.ObjectId(newChatId)}`);
        setChatIdFound(true);
        await startSocketConnection(newChatId);
      }
    } catch (error) {
      console.error("Error getting chat ID:", error);
    }
  };

  const signInAnon = async () => {
    try {
      const { shipment, driver, warehouse } = getUrlParams();
      if (!driver || !warehouse) return;

      setDriverAndWarehouse(driver, warehouse);
      const cred = Realm.Credentials.anonymous(true);
      if (!(await atlasApp.logIn(cred))) return;

      // const result = await validateTokens() //todo: put me back;
      // await getChatsId(driver, warehouse, shipment)
      // await fetchChatMessages();
    } catch (error) {
      setAuthenticated(false);
      setPopState("fetchingChats", false);
    }
  };

  const startSocketConnection = async (chat_id: string) => {
    const { shipment, chatId } = getUrlParams();
    console.log({ chatId });
    !messageSocket &&
      setMessageSocket(
        io(
          process.env.NODE_ENV === "development"
            ? "http://localhost:3000/chats"
            : "/chats",
          { auth: { chat_id, shipment } },
        ),
      );
  };

  /**
   * @async
   * @param {string} chat_id
   * @returns {void}
   */
  const updateMessageSeen = async (chat_id: string) => {
    try {
      // const response = await axios.patch('http://localhost:3000/api/chats', {
      //     update: { chat_id, date: Date.now() }
      // });
      // const result = response.data;
      // console.log({ result })
      messageSocket.emit("message::seen", { chat_id, date: Date.now() });
    } catch (error) {}
  };

  /** an atlas utility function to fetch chat-messages and chat-images (if available) (fire and forget) */
  // const { id } = useDataStore();
  const fetchChatMessages = async () => {
    const { chatId, shipment } = getUrlParams();

    try {
      // const id = await atlasApp.currentUser.functions.getChatId(driver, warehouse, shipment);
      const chatMessages = await atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatMessages)
        .find({ chat_id: Oid.ObjectId.createFromHexString(chatId) });

      if (chatMessages.length > 0) {
        setChatsData(chatMessages);
        setChatsLoading(false);
        setPopState("appAlert", false);
        if (!shipment) await updateMessageSeen(chatId);
        await subscribeToChats(chatId, shipment);
      } else {
        setChatsLoading(false);
        setPopState("appAlert", true);
        setPopState("appError", true);
      }
      setTimeout(() => setPopState("appAlert", false), 4000);
    } catch (error) {}
  };

  const refetchChatMessages = async (
    chatId: string,
    driver: boolean,
    ref: string,
  ) => {
    try {
      const chatMessages = await atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatMessages)
        .find({
          chat_id: Oid.ObjectId.createFromHexString(chatId),
          shipment: ref,
        });

      setChatsData(chatMessages);
      setChatsLoading(false);
      if (driver) await updateMessageSeen(chatId); //updates 'seen' is chat is Driver-Warehouse.
    } catch (error) {}
  };

  const fetchChatMessageImages = async (chatMessageId: string) => {
    try {
      const chatMessageImage = await atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatImages)
        .find({ message: chatMessageId });
      return (chatMessageImage as Partial<IImages[]>)[0]?.image;
    } catch (error) {}
  };

  /** an atlas utility function to subscribe chat-messages */
  const subscribeToChats = async (chatId: string, ref: string) => {
    try {
      const changeStream = atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatMessages)
        .watch({
          filter: {
            "fullDocument.chat_id": Oid.ObjectId.createFromHexString(chatId),
            "fullDocument.shipment": ref,
          },
        });
      if (changeStream) {
        for await (const changeEvent of changeStream) {
          //@ts-ignore
          const { operationType, fullDocument } = changeEvent;
          const { shipment, chatId } = getUrlParams();
          if (fullDocument.source == "DRIVER") {
            await refetchChatMessages(chatId, true, ref);
          } else if (["WAREHOUSE", "CUSTOMER"].includes(fullDocument?.source)) {
            await refetchChatMessages(chatId, false, ref);
          }
        }
      }
    } catch (error) {
      //todo: report to an Error class, which reconnects the realtime subscription
    }
  };

  /** an atlas utility function to send chat-messages (if available) (fire and forget) */
  const sendChatMessage = async (message: Partial<IMessage>) => {
    try {
      return await atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatMessages)
        .insertOne({ ...message });
    } catch (error) {
      // console.log({ error })
    }
  };

  /** an atlas utility function to send chat-message-images (if available) (fire and forget) */
  const sendChatsMessageImages = async (
    images: Partial<IImages>[],
    message: Partial<IMessage>,
  ) => {
    try {
      await atlasApp.currentUser
        .mongoClient(appConfig.appCluster)
        .db(appConfig.databaseName)
        .collection(appConfig.databaseColl.chatImages)
        .insertMany(images);
      await sendChatMessage(message);
    } catch (error) {
      // console.log({ error })
    }
  };

  const retryValidation = async () => {
    try {
      const { driver, warehouse } = getUrlParams();
      if (!driver || !warehouse) return;

      const { env, token } = fetchUrlTOken();
      const revalidateResult =
        await atlasApp.currentUser?.functions.validateRCToken(token, env);
      if (ValidateErrors.includes(revalidateResult?.result)) {
        console.log("Error validating tokens:", revalidateResult?.result);
        setAuthenticated(false);
        setValidationFailed(true);
        setPopState("validation", true);
        return false;
      }
      console.log({ revalidateResult });
      setAuthenticated(true);
      return true;
    } catch (error) {
      // console.log("Error revalidating tokens:", error)
    }
  };

  const authContextValue = useMemo(
    () =>
      ({
        user,
        isLoading,
        signInAnon,
        setIsLoading,
        fetchChatMessages,
        fetchChatMessageImages,
        sendChatMessage: sendChatMessage,
        retryValidation: retryValidation,
        sendChatsMessageImages: sendChatsMessageImages,
      }) as unknown as AtlasAuthContextType,
    [user, isLoading, signInAnon, setIsLoading],
  );

  return atlasApp ? (
    <AtlasAuthContext.Provider value={authContextValue}>
      {children}
    </AtlasAuthContext.Provider>
  ) : null;
}

export default AtlasAuthProvider;
